/**
 * Current-customer context + hook. The provider (CustomerProvider) lives in
 * `src/components/CustomerProvider.tsx`; the split keeps the provider file a
 * component-only module (Vite fast-refresh), like useToast / SnackbarProvider.
 */
import { createContext, useContext } from "react";
import type { CustomerSummary } from "../lib/api";

/** Who edits the current customer: me, or someone else (named). `known` is
 *  false until the server has answered once; the UI then trusts the server. */
export type EditState = {
  canEdit: boolean;
  /** the editor's name when it is not me */
  editor: string | null;
  known: boolean;
};

export type CustomerState = {
  /** the customers this person may work on (active ones only) */
  customers: CustomerSummary[];
  /** the one every page and action works on; null until chosen or when none */
  current: CustomerSummary | null;
  /** still loading the list */
  loading: boolean;
  select: (id: string) => void;
  /** re-read the list, e.g. after an administrator created a customer */
  refresh: () => Promise<void>;
  /** one person edits a customer at a time; everyone else looks on */
  edit: EditState;
};

export const CustomerCtx = createContext<CustomerState | null>(null);

export function useCustomer(): CustomerState {
  const ctx = useContext(CustomerCtx);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
}

const NO_EDIT: EditState = { canEdit: true, editor: null, known: false };

/** The edit state of the current customer; "unknown" outside a
 *  CustomerProvider (component tests), where the server is the only guard. */
export function useEditMode(): EditState {
  return useContext(CustomerCtx)?.edit ?? NO_EDIT;
}

/** The current customer, or null outside a CustomerProvider. */
export function useCurrentCustomer(): CustomerSummary | null {
  return useContext(CustomerCtx)?.current ?? null;
}
